/**
 * ASTUDIO Job Application Form - Global Configuration
 *
 * This file centralizes all static configuration, URLs, and app settings.
 * Update values here rather than hardcoding them throughout the application.
 */

// ============================================
// API ENDPOINTS & WEBHOOKS
// ============================================
export const apiConfig = {
  cvParserWebhook: process.env.REACT_APP_CV_PARSER_WEBHOOK,
  formSubmitWebhook: process.env.REACT_APP_FORM_SUBMIT_WEBHOOK,
  timeout: 30000, // 30 seconds for all API requests
};

// ============================================
// AIRTABLE CONFIGURATION
// ============================================
export const airtableConfig = {
  apiKey: process.env.REACT_APP_AIRTABLE_API_KEY,
  baseId: process.env.REACT_APP_AIRTABLE_BASE_ID,
  fieldsTable: process.env.REACT_APP_AIRTABLE_FIELDS_TABLE || 'Position Fields',

  // Airtable table names
  tables: {
    positions: 'Job Positions',
    countries: 'All Countries',
    ats: 'ATS',
    shortFormApplicant: 'Short Form Applicant',
    positionFields: process.env.REACT_APP_AIRTABLE_FIELDS_TABLE || 'Position Fields',
  },
};

// ============================================
// FILE UPLOAD SETTINGS
// ============================================
export const fileConfig = {
  cv: {
    maxSize: 5 * 1024 * 1024, // 5MB in bytes
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    allowedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeMB: 5,
  },
  photo: {
    maxSize: 2 * 1024 * 1024, // 2MB in bytes
    allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'],
    allowedExtensions: ['jpg', 'jpeg', 'png'],
    maxSizeMB: 2,
  },
  general: {
    maxSize: 10 * 1024 * 1024, // 10MB for other files
    maxSizeMB: 10,
  }
};

// ============================================
// FORM FIELD OPTIONS
// ============================================
export const formOptions = {
  // Languages for spoken/written language fields
  languages: [
    'English',
    'Arabic',
    'French',
    'Spanish',
    'German',
    'Italian',
    'Russian',
    'Chinese (Mandarin)',
    'Japanese',
    'Korean',
    'Portuguese',
    'Dutch',
    'Turkish',
    'Hindi',
    'Urdu',
    'Tagalog',
    'Other'
  ],

  // Hiring sources (fallback if Airtable fails)
  hiringSources: [
    'ASTUDIO - Website',
    'Behance',
    'ChatGPT',
    'Cloud Source',
    'Facebook',
    'Facebook groups',
    'Fiver',
    'GitHub',
    'Glassdoor',
    'Google Search',
    'Guru',
    'Headhunted',
    'Indeed',
    'Instagram',
    'Instagram Ads',
    'LinkedIn',
    'LinkedIn Organic Post',
    'NaukriGulf',
    'Recruitment Agency',
    'Referral/Friends',
    'Tik Tok',
    'Upwork',
    'Vimeo',
    'WhatsApp Group',
    'WhatsApp',
    'Others'
  ],

  // Education levels (fallback if Airtable fails)
  educationLevels: [
    'High School Diploma',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctorate (PhD)',
    'Professional Certification',
    'Other'
  ],

  // Marital status options
  maritalStatuses: ['Single', 'Married'],

  // Gender options
  genders: [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' }
  ],

  // Currency options (auto-detected based on IP)
  currencies: [
    { value: 'AED', label: 'AED (UAE Dirham)', symbol: 'AED' },
    { value: 'USD', label: 'USD (US Dollar)', symbol: '$' }
  ],

  // When can you join
  joinTimes: [
    'Immediately',
    'Within 1 week',
    'Within 2 weeks',
    'Within 1 month',
    'More than 1 month'
  ],

  // Employment types
  employmentTypes: [
    'Full Time',
    'Project base / Freelance'
  ],

  // UAE residence options
  uaeResidence: ['Yes', 'No'],
};

// ============================================
// VALIDATION PATTERNS
// ============================================
export const validationConfig = {
  // Patterns to identify invalid/placeholder values
  invalidValuePatterns: [
    'not provided',
    'n/a',
    'none',
    'please select',
    'select',
    'choose',
    'please',
    'select a',
    'success',
    'error',
    'null',
    'undefined'
  ],

  // Keywords for full-width form fields
  fullWidthKeywords: [
    'summary',
    'description',
    'note',
    'comment',
    'detail',
    'additional'
  ],

  // Email regex pattern
  emailPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  // Phone regex pattern (basic)
  phonePattern: /^[+]?[\d\s-()]+$/,

  // URL pattern
  urlPattern: /^https?:\/\/.+/,
};

// ============================================
// COMPANY & APP METADATA
// ============================================
export const appMetadata = {
  companyName: 'ASTUDIO',
  companyWebsite: 'https://astudio.agency',
  ndaDocumentUrl: 'https://astudio.agency/documents/nda.pdf',

  // Form step configuration
  steps: [
    { number: 1, title: 'CV Upload', key: 'cv-upload' },
    { number: 2, title: 'Basic Info', key: 'basic-info' },
    { number: 3, title: 'Professional Experience', key: 'professional-experience' }
  ],

  // Success message configuration
  successMessage: {
    title: 'Application Submitted Successfully!',
    message: 'Thank you for applying to ASTUDIO. We have received your application and will review it shortly. If your profile matches our requirements, we will contact you soon.',
  },

  // Loading messages
  loadingMessages: {
    parsingCV: 'Parsing your CV...',
    submittingForm: 'Submitting your application...',
    loadingFields: 'Loading form fields...',
  }
};

// ============================================
// UI CONFIGURATION
// ============================================
export const uiConfig = {
  // Theme colors
  colors: {
    primary: '#000000',
    secondary: '#6c757d',
    success: '#28a745',
    danger: '#dc3545',
    warning: '#ffc107',
    yellow: '#fdc936', // ASTUDIO brand yellow
    lightGray: '#f8f9fa',
    borderColor: '#d1d1d1',
  },

  // Notification duration (milliseconds)
  notificationDuration: 5000,

  // Debounce time for auto-save (if implemented)
  autoSaveDebounce: 2000,
};

// ============================================
// FEATURE FLAGS
// ============================================
export const featureFlags = {
  enableCVParsing: true,
  enableAutoFill: true,
  enableFieldValidation: true,
  enableProgressSave: false, // Save progress to localStorage
  enableAnalytics: false, // Track form analytics
  debugMode: process.env.NODE_ENV === 'development',
};

// ============================================
// MAIN CONFIG EXPORT (Backward Compatibility)
// ============================================
export const config = {
  // Legacy exports for backward compatibility
  cvParserWebhook: apiConfig.cvParserWebhook,
  formSubmitWebhook: apiConfig.formSubmitWebhook,
  airtable: airtableConfig,

  // New structured exports
  api: apiConfig,
  files: fileConfig,
  options: formOptions,
  validation: validationConfig,
  metadata: appMetadata,
  ui: uiConfig,
  features: featureFlags,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get file size in human-readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if a value is a placeholder/invalid value
 */
export const isInvalidValue = (value) => {
  if (!value || value === '' || value === null || value === undefined) {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  const valueStr = value.toString().toLowerCase().trim();
  return validationConfig.invalidValuePatterns.some(pattern =>
    valueStr.includes(pattern)
  );
};

/**
 * Get allowed file types as a string for input accept attribute
 */
export const getAcceptString = (fileType = 'cv') => {
  const fileTypes = fileConfig[fileType]?.allowedTypes || [];
  const extensions = fileConfig[fileType]?.allowedExtensions || [];
  return [...fileTypes, ...extensions.map(ext => `.${ext}`)].join(',');
};

/**
 * Validate file based on config
 */
export const validateFile = (file, fileType = 'cv') => {
  const typeConfig = fileConfig[fileType];

  if (!typeConfig) {
    return { valid: false, error: 'Invalid file type configuration' };
  }

  if (file.size > typeConfig.maxSize) {
    return {
      valid: false,
      error: `File size must be less than ${typeConfig.maxSizeMB}MB`
    };
  }

  if (!typeConfig.allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Only ${typeConfig.allowedExtensions.join(', ').toUpperCase()} files are allowed`
    };
  }

  return { valid: true };
};

// Export default for convenience
export default config;